import {useTranslation} from "react-i18next";
import {FarmUnitData, Parcel} from "../../types/domain/parcel";
import {
    MRT_ColumnDef,
    MRT_Row,
    MRT_ShowHideColumnsButton,
    MRT_TableInstance,
    MRT_ToggleFullScreenButton,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import {activeMaderaFarmUnitsLoadable} from "../../store/activeFarmMaderaParcelsStore";
import {isNumber} from "lodash";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import IconButton from "@mui/material/IconButton";
import {getCompanyMaderaReport} from "../../services/companyService";
import {useAtom, useAtomValue} from "jotai";
import {MaderaReportLink, ReportType} from "../../types/domain/maderaReports";
import {Alert, Box, Button, Stack, Tooltip, Typography} from "@mui/material";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {useEffect, useState} from "react";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {MADERA_PARENT_COMPANY} from "../../utils/maderaUtils.ts";
import {useParams} from "react-router-dom";
import {MaderaColumnHeader} from "./MaderaColumnHeader";
import {downloadCsv} from "../../utils/downloadUtils";

export const MaderaFarmUnitTable: React.FC = () => {
    const {t} = useTranslation();
    const farmUnits = useAtomValue(activeMaderaFarmUnitsLoadable);
    const {companyId} = useParams();
    const [enableToolbar, setEnableToolbar] = useState(false);
    const [error, setError] = useState<undefined | string>(undefined);
    const isChildCompany = companyId != undefined && companyId != MADERA_PARENT_COMPANY;

    const handleDownload = async (row: Parcel | FarmUnitData): Promise<void> => {
        if (companyId !== undefined) {
            try {
                const data: MaderaReportLink = await getCompanyMaderaReport({
                    companyId,
                    reportType: ReportType.FARM_UNIT,
                    farmUnit: row.farmUnitName,
                });
                window.open(data.url, "_blank");
            } catch (error) {
                if (error instanceof Error) {
                    setError(`Error downloading report: ${error.message}`);
                } else {
                    setError("An unknown error occurred");
                }
            }
        }
    };
    const handleDownloadWrapper = (row: FarmUnitData) => {
        handleDownload(row).catch((error) => console.error("Error in handleDownload:", error));
    };

    const handleDownloadAccountReport = () => {
        if (companyId !== undefined) {
            getCompanyMaderaReport({
                companyId: companyId,
                reportType: ReportType.ACCOUNT,
            })
                .then((data) => {
                    window.open(data.url, "_blank");
                })
                .catch((error: unknown) => {
                    if (error instanceof Error) {
                        setError(`Error downloading report: ${error.message}`);
                    } else {
                        setError("An unknown error occurred");
                    }
                });
        }
    };

    const handleExport = () => {
        if (farmUnits.state !== "hasData") return;

        const rows: string[][] = [columns.map((c) => c.header)];
        farmUnits.data.forEach((fu) =>
            rows.push([
                fu.farmUnitName,
                fu.assessedAcreage.toString() ?? "",
                fu.irrigationAcreage.toString() ?? "",
                fu.allocation.toString() ?? "",
                fu.carryover.toString() ?? "",
                fu.adjustment.toString() ?? "",
                fu.totalAllocation.toString() ?? "",
                isNumber(fu.etaw) ? (Math.round(fu.etaw * 100) / 100).toString() : "",
                isNumber(fu.etaw) ? (Math.round((fu.totalAllocation - (fu.etaw ?? 0)) * 100) / 100).toString() : "",
                isNumber(fu.etaw)
                    ? Math.round(((fu.totalAllocation - (fu.etaw ?? 0)) / fu.totalAllocation) * 10000) / 100 + "%"
                    : "",
            ]),
        );
        downloadCsv(rows, "budget-table-farm-units.csv");
    };

    const columns: MRT_ColumnDef<FarmUnitData>[] = [
        {
            id: "name",
            header: t("insights.madera.table.farm_unit"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.farm_unit")}
                    tooltip={t("insights.madera.table.tooltips.company.farm_unit")}
                />
            ),
            accessorKey: "farmUnitName",
            minSize: 250,
        },
        {
            id: "assessedAcreage",
            header: t("insights.madera.table.assessed_acreage"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.assessed_acreage")}
                    tooltip={t("insights.madera.table.tooltips.company.assessed_acreage")}
                />
            ),
            accessorKey: "assessedAcreage",
            maxSize: 100,
        },
        {
            id: "irrigationAcreage",
            header: t("insights.madera.table.irrigation_acreage"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.irrigation_acreage")}
                    tooltip={t("insights.madera.table.tooltips.company.irrigation_acreage")}
                />
            ),
            accessorKey: "irrigationAcreage",
            maxSize: 100,
        },
        {
            id: "allocation",
            header: t("insights.madera.table.allocation"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.allocation")}
                    tooltip={t("insights.madera.table.tooltips.company.allocation")}
                />
            ),
            accessorKey: "allocation",
            maxSize: 100,
        },
        {
            id: "carryover",
            header: t("insights.madera.table.carryover"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.carryover")}
                    tooltip={t("insights.madera.table.tooltips.company.carryover")}
                />
            ),
            accessorKey: "carryover",
            maxSize: 100,
        },
        {
            id: "adjustment",
            header: t("insights.madera.table.adjustment"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.adjustment")}
                    tooltip={t("insights.madera.table.tooltips.company.adjustment")}
                />
            ),
            accessorKey: "adjustment",
            maxSize: 100,
        },
        {
            id: "totalAllocation",
            header: t("insights.madera.table.total"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.total")}
                    tooltip={t("insights.madera.table.tooltips.company.total")}
                />
            ),
            accessorKey: "totalAllocation",
            maxSize: 100,
        },
        {
            id: "etaw",
            header: t("insights.madera.table.etaw"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.etaw")}
                    tooltip={t("insights.madera.table.tooltips.company.etaw")}
                />
            ),
            accessorFn: (row) => (isNumber(row.etaw) ? Math.round(row.etaw * 100) / 100 : undefined),
            maxSize: 100,
        },
        {
            id: "remaining",
            header: t("insights.madera.table.remaining"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.remaining")}
                    tooltip={t("insights.madera.table.tooltips.company.remaining")}
                />
            ),
            accessorFn: (row) =>
                isNumber(row.etaw) ? Math.round((row.totalAllocation - (row.etaw ?? 0)) * 100) / 100 : undefined,
            maxSize: 100,
        },
        {
            id: "remainingpct",
            header: t("insights.madera.table.remaining_pct"),
            Header: () => (
                <MaderaColumnHeader
                    label={t("insights.madera.table.remaining_pct")}
                    tooltip={t("insights.madera.table.tooltips.company.remaining_pct")}
                />
            ),
            accessorFn: (row) =>
                isNumber(row.etaw)
                    ? Math.round(((row.totalAllocation - (row.etaw ?? 0)) / row.totalAllocation) * 10000) / 100 + "%"
                    : undefined,
            maxSize: 100,
        },
    ];

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<FarmUnitData>(locale);

    const renderDownloadButton = (row: MRT_Row<FarmUnitData>) => {
        if (isChildCompany && row.original.enableDownloadReport)
            return (
                <IconButton color="primary" onClick={() => handleDownloadWrapper(row.original)}>
                    <DownloadIcon />
                </IconButton>
            );

        return (
            <Tooltip title={t("insights.madera.table.tooltip_disabled_download")}>
                <span>
                    <IconButton disabled={true}>
                        <DownloadIcon />
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    const table: MRT_TableInstance<FarmUnitData> = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 12rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnPinning: {
                right: ["mrt-row-actions"],
            },
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
        },
        columns,
        data: farmUnits.state === "hasData" ? farmUnits.data : [],
        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
                width: "1rem",
            },
        },
        enableColumnResizing: true,
        displayColumnDefOptions: {
            "mrt-row-actions": {
                enableResizing: false,
                size: 30,
                header: "",
            },
        },
        enableToolbarInternalActions: enableToolbar,
        enableTopToolbar: enableToolbar,
        muiTableHeadRowProps: () => ({sx: {backgroundColor: "#F3F5FA"}}),
        renderRowActions: ({row}) => <Box sx={{display: "flex", gap: "1rem"}}>{renderDownloadButton(row)}</Box>,
        state: {
            isLoading: farmUnits.state === "loading",
        },
    });

    const isFullScreen = table.getState().isFullScreen;
    useEffect(() => {
        setEnableToolbar(table.getState().isFullScreen);
    }, [table, isFullScreen]);

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" height="4rem" alignItems="center">
                    <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                        Madera
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Button
                        sx={{whiteSpace: "nowrap"}}
                        startIcon={<DownloadIcon />}
                        disabled={farmUnits.state !== "hasData"}
                        onClick={() => {
                            handleExport();
                        }}
                    >
                        {t("insights.farm_insights_view.export")}
                    </Button>
                    {isChildCompany && (
                        <Button
                            sx={{whiteSpace: "nowrap"}}
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                handleDownloadAccountReport();
                            }}
                        >
                            {t("insights.farm_insights_view.download_report")}
                        </Button>
                    )}
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleFullScreenButton table={table} />
                </Stack>
            </Stack>
            {error && (
                <Alert severity="error" sx={{marginBottom: "1rem"}} onClose={() => setError(undefined)}>
                    {error}
                </Alert>
            )}
            <MaterialReactTable table={table} />
        </>
    );
};
